/* eslint-disable @typescript-eslint/no-use-before-define */
import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { STRAPI_USER_STORAGE_KEY } from '../../constants'
import { Maybe, UsersPermissionsUser, UsersPermissionsUserEntity } from '../../generated/graphql'
import { useBrowserStorage } from '../../hooks'
import strapiHelpers, { RegisterExtraPayload, StrapiLoginPayload } from '../../utils/strapiHelpers'
import posthog from 'posthog-js'

type AuthProviderProps = {
  logout: () => void
  isAuthenticating: boolean
  isAuthenticated: boolean
  user: UsersPermissionsUserEntity
  register: (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    companyName: string,
    extra?: RegisterExtraPayload
  ) => Promise<void>
  login: (identifier: string, password: string, rememberMe: boolean) => Promise<void>
  setUser: React.Dispatch<React.SetStateAction<UsersPermissionsUserEntity | undefined>>
  analyticsAuthInfo: {
    getAuthUrl: (url: string | undefined) => Promise<string>
    baseUrl: Maybe<string> | undefined
  }
}

const AuthContext = React.createContext<Partial<AuthProviderProps>>({})

export const useAuthContext = () => React.useContext(AuthContext)

type UserStorage = StrapiLoginPayload | null

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [localUser, setLocalUser, removeLocalUser] = useBrowserStorage<UserStorage>(
    STRAPI_USER_STORAGE_KEY,
    'local'
  )

  const [sessionUser, setSessionUser, removeSessionUser] = useBrowserStorage<UserStorage>(
    STRAPI_USER_STORAGE_KEY,
    'session'
  )

  const [isAuthenticated, setIsAuthenticated] = React.useState(false)
  const [isAuthenticating, setIsAuthenticating] = React.useState(true)

  /**
   * Storage always holds the *raw* Strapi user under `user` — never the
   * `{ id, attributes }` entity wrapper the rest of the app works with.
   *
   * This used to differ per branch: the "remember me" path wrote the wrapped
   * entity back, so on the next page load `attributes` became `{ id,
   * attributes }`, `user.attributes.email` read as undefined, and each reload
   * nested it one level deeper — steadily growing the stored blob. Keeping one
   * shape in storage and one in state removes the whole class of problem.
   */
  const toEntity = (raw?: StrapiLoginPayload['user']): UsersPermissionsUserEntity | undefined => {
    if (!raw) return undefined

    // Sessions written by the previous version can hold one or more layers of
    // `{ id, attributes }` wrapping. Unwrap them so an already-signed-in
    // customer is repaired on their next load rather than having to sign out.
    let value = raw as Record<string, unknown>
    while (value && typeof value === 'object' && 'attributes' in value && value.attributes) {
      value = value.attributes as Record<string, unknown>
    }

    return {
      id: (value as { id?: string })?.id ?? (raw as { id?: string })?.id,
      attributes: value as unknown as UsersPermissionsUser
    } as UsersPermissionsUserEntity
  }

  const [user, setUser] = React.useState(() =>
    toEntity(sessionUser?.user ?? localUser?.user ?? undefined)
  )

  const persistUser = React.useCallback(
    (data: StrapiLoginPayload, rememberMe?: boolean) => {
      rememberMe ? setLocalUser(data) : setSessionUser(data)
      setUser(toEntity(data?.user))
    },
    [setLocalUser, setSessionUser]
  )

  const logout = React.useCallback(() => {
    removeLocalUser()
    removeSessionUser()
    setIsAuthenticated(false)
    setUser(undefined)
    posthog.reset()
  }, [removeLocalUser, removeSessionUser])

  React.useEffect(() => {
    if (user?.attributes) {
      /**
       * This is where we'd add more user properties to the identify call
       * if we need to track more user properties in PostHog
       * However, historical events will not be updated with these new properties
       */

      posthog.identify(user?.attributes.email as string, {
        ...user.attributes
      })
      setIsAuthenticated(true)

      // Mirror in-memory edits (e.g. a profile update) back into whichever
      // store this session came from, in the raw shape described above.
      const raw = user.attributes as StrapiLoginPayload['user']
      if (localUser?.user && !isEqual(localUser.user, raw)) {
        setLocalUser({ jwt: localUser.jwt, user: raw })
      }
      if (sessionUser?.user && !isEqual(sessionUser.user, raw)) {
        setSessionUser({ jwt: sessionUser.jwt, user: raw })
      }
    }
    setIsAuthenticating(false)
    // eslint-disable-next-line
  }, [user])

  const login = React.useCallback(
    async (email: string, password: string, rememberMe: boolean) => {
      const { data } = await strapiHelpers.login(email, password)
      persistUser(data, rememberMe)
    },
    [persistUser]
  )

  const register = React.useCallback(
    async (
      firstName: string,
      lastName: string,
      email: string,
      password: string,
      companyName: string,
      extra?: RegisterExtraPayload
    ) => {
      const { data } = await strapiHelpers.register(
        firstName,
        lastName,
        email,
        password,
        companyName,
        extra
      )
      persistUser(data)
    },
    [persistUser]
  )

  // Every authenticated screen consumes this context. Handing it a fresh object
  // literal on each render re-rendered all of them for no reason.
  const value = React.useMemo(
    () => ({
      login,
      register,
      logout,
      isAuthenticated,
      isAuthenticating,
      user,
      setUser
    }),
    [login, register, logout, isAuthenticated, isAuthenticating, user]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider
